import React, { useCallback, useMemo } from 'react';
import { Control, UseFormWatch, useFieldArray } from 'react-hook-form';

import Travellers, { TravellersActionButton } from '@ott/travellers';
import { l10n } from '@ott/l10n';
import PlusIcon from '@ott/icon-plus';

import getMultiroomParamsByConfig from '../../utilities/getMultiroomParamsByConfig';
import getGuestsCount from '../../utilities/getGuestsCount';
import { MAX_ROOMS, DEFAULT_ADULTS, trackingTypes } from '../../constants';
import RoomPicker from './blocks/RoomPicker';
import { FormState, HotelsConfig, RenderContext } from '../../types';

import styles from './RoomsPicker.scss';
import { useDispatch } from 'react-redux';

interface Props {
  triggerClassName: string;
  control: Control<FormState>;
  watch: UseFormWatch<FormState>;
  hotelsConfig: HotelsConfig;
  context: RenderContext;
}

const travellersDataLocators = {
  container: 'guest-room-selector',
  input: 'guest-room-selector-trigger',
  overlay: 'guest-room-selector-overlay',
  applyButton: 'submit-guest-room',
};

const RoomsPicker: React.FC<Props> = (props) => {
  const { triggerClassName, hotelsConfig, control, watch, context } = props;
  const { hasMultiroom, maxGuestsCount } = getMultiroomParamsByConfig(hotelsConfig);
  const dispatch = useDispatch();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rooms',
  });

  const rooms = watch('rooms');

  const guestsCount = getGuestsCount(rooms);
  const roomsCount = rooms.length;

  const triggerValueNode = (
    <span data-locator="guest-room-trigger-value">
      {`${guestsCount} ${l10n('guests', { value: guestsCount })}`}
      <span className={styles.dimmed}>
        {`, ${roomsCount} ${l10n('rooms', { value: roomsCount })}`}
      </span>
    </span>
  );

  const availableGuestsCount = maxGuestsCount - getGuestsCount(rooms, false);

  const addRoom = useCallback(() => {
    if (!maxGuestsCount) {
      append({
        adults: DEFAULT_ADULTS,
        childrenAges: [],
      });

      return;
    }

    append({
      adults: Math.min(availableGuestsCount, DEFAULT_ADULTS),
      childrenAges: [],
    });
  }, [availableGuestsCount, append]);

  const deleteRoom = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const handleTravellersBlur = useCallback(() => {
    dispatch({
      type: trackingTypes.roomsChanged,
      eventData: {
        context,
        rooms,
      },
    });
  }, [context, dispatch, rooms]);

  const handleAddRoomClick = useCallback(() => {
    addRoom();
  }, [addRoom]);

  const handleDeleteRoomClick = useCallback(
    (index) => {
      setTimeout(() => deleteRoom(index), 0);
    },
    [deleteRoom]
  );

  const childrenClassNames = useMemo(
    () => ({
      input: triggerClassName,
    }),
    [triggerClassName]
  );

  const canRenderAddButton =
    hasMultiroom && rooms.length < MAX_ROOMS && (!maxGuestsCount || availableGuestsCount > 0);

  const addButtonNode = canRenderAddButton && (
    <TravellersActionButton
      className={styles.addRoom}
      onClick={handleAddRoomClick}
      iconNode={PlusIcon}
      dataLocator="add-room"
    >
      {l10n('addRoom')}
    </TravellersActionButton>
  );

  const travellersListNode = (
    <>
      <div>
        {fields.map((field, index) => {
          return (
            <RoomPicker
              key={field.id}
              index={index}
              control={control}
              maxGuestsCount={maxGuestsCount}
              onDelete={handleDeleteRoomClick}
              hasHeader={rooms.length > 1}
              rooms={rooms}
            />
          );
        })}
      </div>
      {addButtonNode}
    </>
  );

  return (
    <Travellers
      childrenClassNames={childrenClassNames}
      dataLocators={travellersDataLocators}
      triggerValue={triggerValueNode}
      travellersList={travellersListNode}
      placeholder={l10n('hotelsSearchForm.travellersPlaceholder')}
      usePortalOverlay={true}
      withArrowIcon={true}
      hasDoneButton={true}
      onBlur={handleTravellersBlur}
    />
  );
};

export default RoomsPicker;
